import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import './Dialog.css';

/**
 * Dialog Component
 * Reusable modal dialog for confirmations, alerts, and prompts
 */
const Dialog = ({
    isOpen,
    onClose,
    title,
    message,
    type = 'confirm', // 'confirm', 'alert', 'prompt'
    variant = 'default', // 'default', 'danger', 'success', 'info'
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
    placeholder = '',
    defaultValue = ''
}) => {
    const [inputValue, setInputValue] = useState(defaultValue);

    useEffect(() => {
        if (isOpen) {
            setInputValue(defaultValue);
        }
    }, [isOpen, defaultValue]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                handleCancel();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleConfirm = () => {
        if (type === 'prompt') {
            onConfirm?.(inputValue);
        } else {
            onConfirm?.();
        }
        onClose();
    };

    const handleCancel = () => {
        onCancel?.();
        onClose();
    };

    const getIcon = () => {
        switch (variant) {
            case 'danger':
                return <AlertTriangle size={24} />;
            case 'success':
                return <CheckCircle size={24} />;
            case 'info':
                return <Info size={24} />;
            default:
                return null;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="dialog-overlay" onClick={handleCancel}>
            <div
                className={`dialog-container dialog-${variant}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="dialog-header">
                    {getIcon() && <div className="dialog-icon">{getIcon()}</div>}
                    <h3 className="dialog-title">{title}</h3>
                    <button className="dialog-close" onClick={handleCancel}>
                        <X size={20} />
                    </button>
                </div>

                <div className="dialog-body">
                    <p className="dialog-message">{message}</p>
                    {type === 'prompt' && (
                        <input
                            type="text"
                            className="dialog-input"
                            placeholder={placeholder}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleConfirm();
                                }
                            }}
                            autoFocus
                        />
                    )}
                </div>

                <div className="dialog-footer">
                    {type !== 'alert' && (
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={handleCancel}
                        >
                            {cancelText}
                        </button>
                    )}
                    <button
                        className={`btn btn-sm ${variant === 'danger' ? 'btn-danger' : 'btn-primary'}`}
                        onClick={handleConfirm}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Dialog;
